import { useMemo, useEffect, useState } from 'react';
import type { MarqueePublic } from '../../types';
import type { MarqueeSettings } from '../../types';
import './MarqueeBanner.css';

interface MarqueeBannerProps {
  marquees: MarqueePublic[];
  enabled: boolean;
  settings: MarqueeSettings | null;
  position?: 'top' | 'below-header'; // 'top' for GlobalHeader (default), 'below-header' for MobileHeader
}

/**
 * Device type for responsive marquee behavior
 */
type DeviceType = 'mobile' | 'tablet' | 'desktop';

/**
 * Helper function to convert hex color to rgba with opacity
 */
const bgColorWithOpacity = (color: string, opacity: number): string => {
  const hex = color.replace('#', '');
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
};

/**
 * Get device type based on window width
 */
const getDeviceType = (width: number): DeviceType => {
  if (width < 768) return 'mobile';
  if (width < 1024) return 'tablet';
  return 'desktop';
};

/**
 * Speed multiplier for different device types
 * Mobile is baseline (1x), larger screens need more time to maintain same visual speed
 */
const SPEED_MULTIPLIERS: Record<DeviceType, number> = {
  mobile: 1.0,
  tablet: 1.5,
  desktop: 2.0,
};

/**
 * Spacing divisor for different device types
 * Mobile uses vw/3, larger screens use larger divisors for proportionally smaller gaps
 */
const SPACING_DIVISORS: Record<DeviceType, number> = {
  mobile: 3,
  tablet: 5,
  desktop: 8,
};

/**
 * MarqueeBanner Component
 * Displays scrolling marquees on mobile, tablet, and desktop
 * Uses CSS Animation for smooth horizontal scrolling effect
 * All marquees appear in sequence on a single track
 * Each marquee has its own background and text color
 *
 * Corresponds to UXC-MarqueeFeature-MarqueeBanner-001
 */
const MarqueeBanner = ({ marquees, enabled, settings, position = 'top' }: MarqueeBannerProps) => {
  // State for device type detection
  const [deviceType, setDeviceType] = useState<DeviceType>(() => {
    if (typeof window === 'undefined') return 'mobile';
    return getDeviceType(window.innerWidth);
  });

  // Update device type on resize
  useEffect(() => {
    const handleResize = () => {
      setDeviceType(getDeviceType(window.innerWidth));
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Sort marquees by display order
  const sortedMarquees = useMemo(() => {
    return [...marquees].sort((a, b) => a.displayOrder - b.displayOrder);
  }, [marquees]);

  // Calculate adjusted speed based on device type
  const baseSpeed = settings?.defaultSpeed ?? 15;
  const adjustedSpeed = baseSpeed * SPEED_MULTIPLIERS[deviceType];

  // Calculate spacing based on device type (vw unit)
  const spacingVw = 100 / SPACING_DIVISORS[deviceType];

  // Debug: log device type and speed adjustment
  console.log(`🖥️ [MarqueeBanner] Device: ${deviceType}, Base: ${baseSpeed}s, Adjusted: ${adjustedSpeed}s, Spacing: ${spacingVw}vw`);

  // Don't render when disabled or no marquees
  if (!enabled || marquees.length === 0) {
    return null;
  }

  return (
    <div
      className={`marquee-banner ${position === 'below-header' ? 'marquee-banner--below-header' : ''}`}
      style={{
        '--marquee-duration': `${adjustedSpeed}s`,
        '--marquee-spacing': `${spacingVw}vw`,
        '--marquee-banner-bg': bgColorWithOpacity(
          settings?.defaultBgColor || '#ffffff',
          settings?.defaultBgOpacity ?? 1
        ),
      } as React.CSSProperties}
    >
      <div className="marquee-banner__track">
        {sortedMarquees.map((marquee) => (
          <span
            key={marquee.id}
            className="marquee-banner__content"
            style={{
              '--marquee-text-color': marquee.textColor,
              '--marquee-bg-color': bgColorWithOpacity(marquee.bgColor, marquee.bgOpacity),
            } as React.CSSProperties}
          >
            {marquee.content}
          </span>
        ))}
        {/* Duplicate for seamless loop */}
        {sortedMarquees.map((marquee) => (
          <span
            key={`dup-${marquee.id}`}
            className="marquee-banner__content"
            aria-hidden="true"
            style={{
              '--marquee-text-color': marquee.textColor,
              '--marquee-bg-color': bgColorWithOpacity(marquee.bgColor, marquee.bgOpacity),
            } as React.CSSProperties}
          >
            {marquee.content}
          </span>
        ))}
      </div>
    </div>
  );
};

export default MarqueeBanner;
